/**
 * Draft Management Utility
 * Saves form data to localStorage when user is not authenticated.
 * Restores after login so the original action can continue.
 */

const DRAFT_KEY = 'zenstudios_form_draft';
const PENDING_ACTION_KEY = 'zenstudios_pending_action';

export const saveDraft = (formType, formData, returnPath) => {
  const draft = {
    formType,
    formData,
    returnPath: returnPath || window.location.pathname,
    savedAt: new Date().toISOString(),
  };
  localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
};

export const getDraft = (formType) => {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const draft = JSON.parse(raw);
    if (formType && draft.formType !== formType) return null;
    // Drafts expire after 24 hours
    const savedAt = new Date(draft.savedAt);
    if (Date.now() - savedAt.getTime() > 24 * 60 * 60 * 1000) {
      clearDraft();
      return null;
    }
    return draft;
  } catch {
    return null;
  }
};

export const clearDraft = () => {
  localStorage.removeItem(DRAFT_KEY);
};

export const setPendingAction = (action) => {
  localStorage.setItem(PENDING_ACTION_KEY, JSON.stringify({
    ...action,
    savedAt: new Date().toISOString(),
  }));
};

export const getPendingAction = () => {
  try {
    const raw = localStorage.getItem(PENDING_ACTION_KEY);
    if (!raw) return null;
    const action = JSON.parse(raw);
    const savedAt = new Date(action.savedAt);
    if (Date.now() - savedAt.getTime() > 30 * 60 * 1000) {
      clearPendingAction();
      return null;
    }
    return action;
  } catch {
    return null;
  }
};

export const clearPendingAction = () => {
  localStorage.removeItem(PENDING_ACTION_KEY);
};

/**
 * Auth gate: checks if user is logged in. If not, saves draft and redirects to login.
 * @param {boolean} isAuthenticated
 * @param {string} formType - e.g. 'list_property', 'interior_quote', 'contact_developer'
 * @param {object} formData - current form state
 * @param {function} navigate - react-router navigate
 * @returns {boolean} true if authenticated, false if redirected
 */
export const requireAuthForAction = (isAuthenticated, formType, formData, navigate) => {
  if (isAuthenticated) return true;
  saveDraft(formType, formData, window.location.pathname);
  setPendingAction({ type: formType, returnPath: window.location.pathname });
  navigate('/login', { state: { from: window.location.pathname, pendingAction: formType } });
  return false;
};
